import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  from,
  InMemoryCache,
  type ServerError,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { datadogLogs } from '@datadog/browser-logs';
import { useUpdateEffect } from '@react-hookz/web';
import { uniqueGraphQLErrorsByMessage } from '@utils/graphQLError';
import {
  buildGraphQLErrorToast,
  buildNetworkErrorToast,
} from '@utils/graphQLErrorToast';
import { useSnackbar } from 'hephaestus/web';
import type React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { CONFIG } from '../config';
import { useLocalization } from '../hooks/useLocalization';
import { clientHostedLogout } from './AuthProvider';
import { auth0Logout, navigateToCrossTabLogoutLanding } from './auth0Client';
import {
  CROSS_TAB_LOGOUT_KEY,
  captureLogoutBroadcastTimestamp,
  invalidateSessionEpoch,
  isLogoutBroadcastPending,
  markLogoutBroadcastProcessed,
  restoreOrBroadcastLogout,
  shouldProcessLogoutBroadcast,
  subscribeToCrossTabLogout,
} from './crossTabLogout';

const PUBLIC_ENDPOINTS = [
  'fetchOrganizationPublicSettings',
  'exchangeClientToken',
];

type AuthContextType = {
  isEmbedded: boolean;
  setIsEmbedded: (embedded: boolean) => void;
  settings: AuthContextSettings;
  setSettings: (settings: AuthContextSettings) => void;
  permissions: string[];
  entityRoleIdRef: { current: string | undefined };
  getTokenSilentlyRef: { current: (() => Promise<string>) | undefined };
  setPermissions: (permissions: string[]) => void;
  appLogout: (input?: {
    reason?: string;
    preventSingleLogout?: boolean;
  }) => Promise<void>;
};

export type AuthContextSettings = {
  clientHostedLoginRedirectUri?: string;
  clientHostedLoginURL?: string;
  clientHostedLoginClientId?: string;
  subdomain?: string;
  auth0RedirectUri?: string;
  enableInactivityTimeOut?: boolean;
  inactivityTimeoutInMinutes?: number;
  useHostedLogin?: boolean;
  logoutUrl?: string;
  enableSingleLogout?: boolean;
};

const AuthContext = createContext<AuthContextType>({
  isEmbedded: false,
  setIsEmbedded: () => {},
  settings: {},
  setSettings: () => {},
  permissions: [],
  setPermissions: () => {},
  appLogout: async (
    _input: { reason?: string; preventSingleLogout?: boolean } = {},
  ) => {},
  entityRoleIdRef: { current: undefined },
  getTokenSilentlyRef: { current: undefined },
});

export const cache: InMemoryCache = new InMemoryCache({
  typePolicies: { GroupedBilling: { keyFields: ['id', 'billingCycleId'] } },
});

const OvApolloProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { translateBackendString, language } = useLocalization();
  const { t } = useTranslation(['shared']);
  const { enqueueSnackbar } = useSnackbar();
  const [resetKey, setResetKey] = useState(0);
  const [settings, setSettings] = useState<AuthContextSettings>({});
  const [isEmbedded, setIsEmbedded] = useState(false);
  const [permissions, setPermissions] = useState<string[]>([]);

  const getTokenSilentlyRef = useRef<(() => Promise<string>) | undefined>();
  const isAppLogoutRef = useRef(false);
  const entityRoleIdRef = useRef<string>();
  const languageRef = useRef(language);
  languageRef.current = language;

  const appLogout = async ({
    reason,
    preventSingleLogout,
  }: {
    reason?: string;
    preventSingleLogout?: boolean;
  } = {}) => {
    isAppLogoutRef.current = true;

    let preservedLogoutBroadcast: string | null = null;

    if (!isEmbedded) {
      const redirectUrl = settings?.logoutUrl ?? settings?.auth0RedirectUri;
      const federated = settings?.enableSingleLogout && !preventSingleLogout;
      const auth0DidBroadcast = await auth0Logout({ redirectUrl, federated });
      if (auth0DidBroadcast) {
        preservedLogoutBroadcast = captureLogoutBroadcastTimestamp();
      }
    }

    if (settings?.clientHostedLoginURL && settings?.useHostedLogin) {
      await clientHostedLogout(
        settings?.clientHostedLoginURL,
        settings?.logoutUrl ?? settings?.clientHostedLoginRedirectUri,
        reason,
      );
    }
    if (client) await client.clearStore();
    if (window?.localStorage) {
      window.localStorage.clear();
      if (reason && reason !== '') {
        window.localStorage.setItem('loggedOutMessage', reason);
      }
    }
    restoreOrBroadcastLogout(preservedLogoutBroadcast);
    setResetKey((prevKey) => prevKey + 1); // Increment the key to force re-mount
    isAppLogoutRef.current = false;
  };

  const client = useMemo(
    () =>
      new ApolloClient({
        cache,
        link: from([
          onError(({ graphQLErrors, networkError, operation }) => {
            let preLogoutMessage = '';
            let userNeedsToBeLoggedOut = false;
            if (networkError) {
              const skipErrorHandler =
                Boolean(operation.variables.skipErrorHandler) ||
                Boolean(operation.getContext().skipErrorHandler);
              const isUnauthorized =
                'statusCode' in networkError &&
                (networkError as ServerError).statusCode === 401;

              if (!isUnauthorized) {
                datadogLogs.logger.error(
                  `Network Error: cause: ${networkError.cause}, message: ${networkError.message}`,
                );
              }

              if (!skipErrorHandler || isUnauthorized) {
                const toast = buildNetworkErrorToast(
                  isUnauthorized ? { statusCode: 401 } : {},
                  t,
                );
                enqueueSnackbar(toast.message, { variant: toast.variant });
              }
            }
            if (graphQLErrors) {
              graphQLErrors.forEach(
                ({ message, locations, path, extensions }) => {
                  console.log(
                    `[GraphQL error]: Code: ${extensions?.code}, Message: ${message}, Operation: ${JSON.stringify(operation.query)}, Variables: ${JSON.stringify(
                      operation.variables,
                    )}, Location: ${JSON.stringify(locations)}, Path: ${path}`,
                  );
                },
              );

              if (
                !operation.variables.skipErrorHandler &&
                graphQLErrors.length > 0
              ) {
                const uniqueErrors =
                  uniqueGraphQLErrorsByMessage(graphQLErrors);
                for (const gqlError of uniqueErrors) {
                  const toast = buildGraphQLErrorToast(
                    translateBackendString,
                    gqlError,
                    t,
                    operation.variables.errorMessage,
                  );
                  if (!toast) continue;

                  if (toast.logout) {
                    console.log(
                      'TOKEN_REFRESH_REQUIRED detected - processing logout',
                      {
                        errorCode: gqlError.extensions?.code,
                        errorMessage: gqlError.message,
                        operationName: operation.operationName,
                        timestamp: new Date().toISOString(),
                      },
                    );
                    userNeedsToBeLoggedOut = true;
                    preLogoutMessage = toast.message;
                  }

                  enqueueSnackbar(toast.message, {
                    variant: toast.variant,
                  });
                }
              }
            }

            if (userNeedsToBeLoggedOut === true) {
              console.warn('User needs to be logged out - scheduling logout', {
                preLogoutMessage,
                errorCode: graphQLErrors?.[0]?.extensions?.code,
                timestamp: new Date().toISOString(),
              });

              setTimeout(() => {
                appLogout();
              }, 5000); // <---- 5 seconds
              datadogLogs.logger.warn(
                'User session expired. Will log out in 10 seconds...',
                {
                  errorType: 'ApolloClientError',
                  step: 'PreLogout',
                  errorMessage: preLogoutMessage,
                  errorCode: graphQLErrors?.[0]?.extensions?.code,
                },
              );
            }
          }),
          setContext(async ({ operationName }, { headers }) => {
            let token: string | undefined;

            try {
              if (
                !(operationName && PUBLIC_ENDPOINTS.includes(operationName))
              ) {
                token = await getTokenSilentlyRef.current?.();
              }
            } catch (error) {
              datadogLogs.logger.error('Token Refresh Error', {
                error,
                operationName,
              });
              if (!isAppLogoutRef.current) {
                isAppLogoutRef.current = true;
                await appLogout();
              }

              throw error;
            }

            return {
              headers: {
                ...headers,
                ...(token && { authorization: `Bearer ${token}` }),
                ...(entityRoleIdRef.current && {
                  'x-role-id': entityRoleIdRef.current,
                }),
                ...(languageRef.current && {
                  'x-language': languageRef.current,
                }),
              },
            };
          }).concat(createHttpLink({ uri: CONFIG.app.graphqlClient })),
        ]),
      }),

    // TODO: To enhance handling for TOKEN_REFRESH_REQUIRED, remove the below 'disable line" and add appLogout
    // to the dependency array this will require a refactor of the appLogout function to be a hook
    // (e.g to wrap it in a useCallback)

    [enqueueSnackbar, t],
  );

  const teardownLocalSession = useCallback((logoutTs: number) => {
    if (isAppLogoutRef.current || !shouldProcessLogoutBroadcast(logoutTs)) {
      return;
    }

    isAppLogoutRef.current = true;
    markLogoutBroadcastProcessed(logoutTs);
    invalidateSessionEpoch();
    navigateToCrossTabLogoutLanding();
  }, []);

  useUpdateEffect(() => {
    void client.refetchQueries({ include: 'active' });
  }, [language]);

  useEffect(() => {
    if (isLogoutBroadcastPending()) {
      const logoutTs = Number(localStorage.getItem(CROSS_TAB_LOGOUT_KEY) || 0);
      void teardownLocalSession(logoutTs);
    }

    return subscribeToCrossTabLogout((logoutTs) => {
      void teardownLocalSession(logoutTs);
    });
  }, [teardownLocalSession]);

  return (
    <AuthContext.Provider
      value={{
        isEmbedded,
        setIsEmbedded,
        permissions,
        setPermissions,
        settings,
        setSettings,
        appLogout,
        entityRoleIdRef,
        getTokenSilentlyRef,
      }}
    >
      <ApolloProvider client={client}>
        <div key={resetKey}>{children}</div>
      </ApolloProvider>
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);

export default OvApolloProvider;
